/** @format */

/**
 * Thin wrapper around the Google Analytics (GA4) `gtag` global. The analytics
 * script is loaded at runtime by `initAnalytics`, and only on production hosts,
 * so all helpers no-op gracefully when `gtag` is unavailable (e.g. during
 * tests, on dev/staging, or before the script has loaded). Callers can fire
 * events without guarding.
 */

import { getEnvironmentFromHostname } from "@question-bank/shared";

export const GA_MEASUREMENT_ID = "G-0XE0QQPGSW";

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

/**
 * Load and initialise Google Analytics (GA4) — but only on production hosts.
 * The same build artifact is deployed to dev, staging and prod, so we detect
 * the environment at runtime from the hostname and skip GA everywhere but prod.
 */
export const initAnalytics = (): void => {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return;
	}
	if (getEnvironmentFromHostname(window.location.hostname) !== "prod") {
		return;
	}
	if (typeof window.gtag === "function") {
		return;
	}

	window.dataLayer = window.dataLayer || [];
	window.gtag = function gtag() {
		// GA's gtag.js reads the raw `arguments` object off dataLayer.
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer!.push(arguments);
	};
	window.gtag("js", new Date());
	// Page views are sent manually on route changes (single-page app).
	window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
	document.head.appendChild(script);
};

const gtag = (...args: unknown[]): void => {
	if (typeof window === "undefined" || typeof window.gtag !== "function") {
		return;
	}
	window.gtag(...args);
};

/** Record a single-page-app navigation as a GA4 `page_view`. */
export const trackPageView = (path: string, title?: string): void => {
	if (typeof window === "undefined") {
		return;
	}
	gtag("event", "page_view", {
		page_path: path,
		page_location: window.location.href,
		page_title: title ?? document.title,
		send_to: GA_MEASUREMENT_ID,
	});
};

/** Record a meaningful user interaction as a GA4 custom event. */
export const trackEvent = (name: string, params: AnalyticsParams = {}): void => {
	gtag("event", name, params);
};
